import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Button } from '@aimsos/ui';

import DataListFilterBar from '../components/DataListFilterBar.jsx';
import DataListFilterSlideout from '../components/DataListFilterSlideout.jsx';
import EntityListSkeleton from '../components/EntityListSkeleton.jsx';
import KnowledgeQuickFilters from '../components/knowledge/KnowledgeQuickFilters.jsx';
import SandboxGroupCard from '../components/knowledge/sandbox/SandboxGroupCard.jsx';
import { AccessDeniedState, EmptyState, ErrorState } from '../components/states/index.js';
import { ITEM_KINDS } from '../constants/itemSlideOut.js';
import {
  KNOWLEDGE_SOURCE_TYPES,
  SANDBOX_QUICK_FILTERS,
  SANDBOX_SEARCH_FIELDS,
  SANDBOX_SORT_DIRECTION,
  SANDBOX_SORT_FIELD,
  SANDBOX_SORTS,
} from '../constants/knowledge.js';
import { useClaimAction } from '../hooks/useClaimAction.js';
import { useDataListFilters } from '../hooks/useDataListFilters.js';
import { useDriveSourceFilter } from '../hooks/useDriveSourceFilter.js';
import { useItemParam } from '../hooks/useItemParam.js';
import { useOpenSandboxSource } from '../hooks/useOpenSandboxSource.js';
import { useSandboxGroups } from '../hooks/useSandboxGroups.js';

import { Inbox, SearchX } from 'lucide-react';

const FILTER_DEFAULTS = Object.freeze({
  defaultSortField: SANDBOX_SORT_FIELD,
  defaultSortDirection: SANDBOX_SORT_DIRECTION,
  searchFields: SANDBOX_SEARCH_FIELDS,
});

/**
 * Knowledge › Sandbox (CT-5718): the record's own Sandbox — what people said and
 * who said it, grouped by source, to confirm. A mirror of Governance Studio's
 * sandbox scoped to this record, with no governance filters.
 *
 * Search, the chips and All filters (Source, Source type — staged) narrow on the
 * server, which also orders the groups (Most recent, Most claims, Conflicts
 * first). Confirm and Send for confirmation act on a row at once and roll back
 * on failure; a claim in conflict opens Compare in the profile slide-out
 * (`?item=claim_compare:<claimId>`). Drive's "View claims" arrives with
 * `?source=<fileId>`, which becomes the Source filter (`useDriveSourceFilter`);
 * a group's Open goes back where it came from (`useOpenSandboxSource`).
 */
function SandboxPage() {
  const { t } = useTranslation('unified-profiles');
  const { entityType, entityId, tabs = [], openTab, itemsVersion = 0, refreshApproach } = useOutletContext();
  const [quickFilter, setQuickFilter] = useState('all');
  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);

  const filtersState = useDataListFilters(FILTER_DEFAULTS);
  const { filters, search, orderBy, orderDirection, hasActiveQuery, clearAll, setFilter } = filtersState;

  useDriveSourceFilter(setFilter);

  const { groups, sourceOptions, loading, error, accessDenied, refetch, refresh, patchClaim } = useSandboxGroups({
    entityType,
    entityId,
    orderBy,
    orderDirection,
    quickFilter,
    filters,
    search,
  });

  // A conflict resolved in the slide-out (outside this page) bumps `itemsVersion`.
  const lastItemsVersionRef = useRef(itemsVersion);
  useEffect(() => {
    if (lastItemsVersionRef.current === itemsVersion) return;
    lastItemsVersionRef.current = itemsVersion;
    refresh();
  }, [itemsVersion, refresh]);

  const { run, runningIds } = useClaimAction({ entityType, entityId, patchClaim, onDone: refreshApproach });
  const { openItem } = useItemParam();
  const handleCompare = useCallback((claim) => openItem(ITEM_KINDS.CLAIM_COMPARE, claim.id), [openItem]);

  const handleOpenSource = useOpenSandboxSource({ tabs, openTab });

  const filterDefs = useMemo(
    () => [
      { id: 'source', label: t('knowledge.sandbox.filters.source'), options: sourceOptions },
      {
        id: 'source_type',
        label: t('knowledge.sandbox.filters.sourceType'),
        options: KNOWLEDGE_SOURCE_TYPES.map((value) => ({ value, label: t(`knowledge.sourceTypes.${value}`) })),
      },
    ],
    [sourceOptions, t],
  );
  const sortDefs = useMemo(() => SANDBOX_SORTS.map((value) => ({ value, label: t(`knowledge.sandbox.sort.${value}`) })), [t]);

  const openAllFilters = useCallback(() => {
    filtersState.openDraft();
    setIsAllFiltersOpen(true);
  }, [filtersState]);
  const closeAllFilters = useCallback(() => setIsAllFiltersOpen(false), []);
  const clearEverything = useCallback(() => {
    clearAll();
    setQuickFilter('all');
  }, [clearAll, setQuickFilter]);

  const results = () => {
    if (loading) return <EntityListSkeleton perPage={4} />;
    if (accessDenied) return <AccessDeniedState />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (!groups.length) {
      return hasActiveQuery || quickFilter !== 'all' ? (
        <EmptyState
          testId="no-results-state"
          icon={SearchX}
          title={t('knowledge.sandbox.noResults.title')}
          subtitle={t('knowledge.sandbox.noResults.subtitle')}
          action={
            <Button variant="secondary" size="sm" onClick={clearEverything}>
              {t('knowledge.sandbox.noResults.clear')}
            </Button>
          }
        />
      ) : (
        <EmptyState icon={Inbox} title={t('knowledge.sandbox.empty.title')} subtitle={t('knowledge.sandbox.empty.subtitle')} />
      );
    }
    return (
      <div className="flex flex-col gap-3">
        {groups.map((group, index) => (
          <SandboxGroupCard
            key={group.id}
            group={group}
            initiallyOpen={index === 0}
            runningIds={runningIds}
            onAction={run}
            onCompare={handleCompare}
            onOpenSource={handleOpenSource}
          />
        ))}
      </div>
    );
  };

  return (
    <div data-testid="sandbox-page" className="flex min-w-0 flex-1 flex-col gap-4 pb-6">
      <DataListFilterBar
        filtersState={filtersState}
        filterDefs={filterDefs}
        sortDefs={sortDefs}
        searchPlaceholder={t('knowledge.sandbox.searchPlaceholder')}
        onAllFiltersClick={openAllFilters}
      />
      <KnowledgeQuickFilters
        ids={SANDBOX_QUICK_FILTERS}
        labelPrefix="knowledge.sandbox.quickFilters"
        value={quickFilter}
        onChange={setQuickFilter}
      />
      <p className="text-sm text-content-secondary">{t('knowledge.sandbox.description')}</p>
      <div className="flex min-w-0 flex-1 flex-col">{results()}</div>
      <DataListFilterSlideout
        isOpen={isAllFiltersOpen}
        onClose={closeAllFilters}
        filtersState={filtersState}
        filterDefs={filterDefs}
        sortDefs={sortDefs}
        showSort={false}
      />
    </div>
  );
}

export default SandboxPage;
